import { ContentLoading } from '@/components/common/content-loading';
import { ChangeRegionButton, NavigationGuide, NearbyPoiListDropdown, VerticalMapHeader, ZoomControlBtnGroup } from '@/components/feature/floor-guide';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon } from '@/components/icons';
import { ParkingSlotList } from '@/components/feature/parking/horizontal/parking-slot-list';
import { NavigationStatus } from '@/enums/floor-guide';
import { useFloorCategory } from '@/hooks/map';
import { useParkingMapView } from '@/hooks/parking';
import { generateFloorName } from '@/utils/floor-guide';
import { generateQrHereUrl } from '@/utils/map';
import { getPoiTitleByLanguage } from '@/utils/map-poi';
import { generateDisplayDirections } from '@/utils/route-simulation';
import clsx from 'clsx';
import styles from './styles.module.scss';

const EMPTY_CATEGORY_ON_FLOOR = { ko: '', en: '', ja: '', zh: '' };

interface VerticalViewProps {
    readonly gnbMenuId?: string;
}

export default function VerticalView({ gnbMenuId }: VerticalViewProps) {
    const {
        t,
        mapContainerRef,
        containerRef,
        parkingSlotListRef,
        server,
        screenMode,
        zoomClosed,
        language,
        mapData,
        loading,
        isMapReady,
        selectedFloorName,
        currentBuildingId,
        isDisabledZoomIn,
        isDisabledZoomOut,
        handleZoomIn,
        handleZoomOut,
        moveUp,
        moveDown,
        moveLeft,
        moveRight,
        showPositionPopup,
        showNavigationGuide,
        navigationStatus,
        selectedPoiRef,
        selectedRoute,
        startPoint,
        navigationGuideEndPoint,
        nearbyPoiList,
        nearbySelectedPoiId,
        nearbyTitle,
        nearbyFloorName,
        parkingSlots,
        selectedSlotId,
        handleSelectSlot,
        handleEndAndReset,
        handleShowMyLocation,
        handleDirections,
        handlePauseSimulation,
        handleResumeSimulation,
        handleWatchAgain,
    } = useParkingMapView(gnbMenuId);

    const { selectedFloor } = useFloorCategory(selectedFloorName, currentBuildingId);

    return (
        <section className={styles['parking-vertical-view']} ref={containerRef}>
            <section className={styles['parking-vertical-view__content']}>
                <VerticalMapHeader floorName={selectedFloor?.name} categoryOnFloor={EMPTY_CATEGORY_ON_FLOOR} isShowAllFloorGuide={false} />

                <div className={styles['parking-vertical-view__map-container']}>
                    <div className="flex-1" style={{ pointerEvents: zoomClosed ? 'auto' : 'none' }}>
                        <div className="h-full w-full" ref={mapContainerRef} />
                    </div>

                    <section className={styles['parking-vertical-view__zoom-control']}>
                        <ZoomControlBtnGroup
                            tabIndex={6}
                            regionOrder={3}
                            disabledZoomIn={isDisabledZoomIn}
                            disabledZoomOut={isDisabledZoomOut}
                            onZoomIn={handleZoomIn}
                            onZoomOut={handleZoomOut}
                        />
                    </section>

                    <div data-region-nav={!zoomClosed} data-region-order={2}>
                        <div
                            tabIndex={-1}
                            className={clsx(styles['parking-vertical-view__change-zoom'], styles['parking-vertical-view__change-zoom--up'], {
                                hidden: zoomClosed,
                                block: !zoomClosed,
                            })}
                        >
                            <ChangeRegionButton
                                tabIndex={2}
                                data-interactive
                                aria-label={t('floor_guide.text_to_speech.move.up')}
                                icon={<ChevronUpIcon />}
                                onClick={moveUp}
                                data-role={t('common.data_role.button')}
                            />
                        </div>
                        <div
                            tabIndex={-1}
                            className={clsx(styles['parking-vertical-view__change-zoom'], styles['parking-vertical-view__change-zoom--left'], {
                                hidden: zoomClosed,
                                block: !zoomClosed,
                            })}
                        >
                            <ChangeRegionButton
                                tabIndex={3}
                                data-interactive
                                aria-label={t('floor_guide.text_to_speech.move.left')}
                                icon={<ChevronLeftIcon />}
                                onClick={moveLeft}
                                data-role={t('common.data_role.button')}
                            />
                        </div>
                        <div
                            tabIndex={-1}
                            className={clsx(styles['parking-vertical-view__change-zoom'], styles['parking-vertical-view__change-zoom--down'], {
                                hidden: zoomClosed,
                                block: !zoomClosed,
                            })}
                        >
                            <ChangeRegionButton
                                tabIndex={4}
                                data-interactive
                                aria-label={t('floor_guide.text_to_speech.move.down')}
                                icon={<ChevronDownIcon />}
                                onClick={moveDown}
                                data-role={t('common.data_role.button')}
                            />
                        </div>
                        <div
                            tabIndex={-1}
                            className={clsx(styles['parking-vertical-view__change-zoom'], styles['parking-vertical-view__change-zoom--right'], {
                                hidden: zoomClosed,
                                block: !zoomClosed,
                            })}
                        >
                            <ChangeRegionButton
                                tabIndex={5}
                                data-interactive
                                aria-label={t('floor_guide.text_to_speech.move.right')}
                                icon={<ChevronRightIcon />}
                                onClick={moveRight}
                                data-role={t('common.data_role.button')}
                            />
                        </div>
                    </div>

                    {showPositionPopup && (
                        <div className={styles['parking-vertical-view__position-popup']}>
                            <NearbyPoiListDropdown
                                type="show-location"
                                isActive={showPositionPopup}
                                poiList={nearbyPoiList}
                                selectedPoiId={nearbySelectedPoiId}
                                title={nearbyTitle}
                                floorName={nearbyFloorName}
                                buildingName=""
                                onSelectPoi={() => {}}
                                onStartNavigationGuide={() => handleDirections()}
                                onClose={handleEndAndReset}
                            />
                        </div>
                    )}

                    <div
                        className={clsx(styles['parking-vertical-view__navigation-guide'], {
                            [styles['parking-vertical-view__navigation-guide--visible']]: showNavigationGuide,
                        })}
                    >
                        <NavigationGuide
                            isActive={showNavigationGuide}
                            isShowPauseBtn={navigationStatus === NavigationStatus.RUNNING}
                            isShowResumeBtn={navigationStatus === NavigationStatus.PAUSED}
                            isShowWatchAgainBtn={navigationStatus === NavigationStatus.DONE}
                            directionMaxVisibleRows={screenMode === 'split' ? 3 : undefined}
                            qrHereUrl={generateQrHereUrl(server?.store?.qrHereId ?? '', startPoint, selectedPoiRef?.id ?? '')}
                            buildingName=""
                            directions={generateDisplayDirections(
                                t,
                                (floorId: string) => {
                                    const floor = mapData?.dataFloor?.getFloors()?.find((f: any) => f.id === floorId);
                                    const floorName = floor?.name?.[0]?.text ?? '';
                                    return t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(floorName) });
                                },
                                selectedRoute?.navigationList,
                                getPoiTitleByLanguage(selectedPoiRef, language)
                            )}
                            startFloorName={server?.floor?.name?.[language] || server?.floor?.name?.['ko'] || ''}
                            endPoint={navigationGuideEndPoint}
                            onStop={handleEndAndReset}
                            onResume={handleResumeSimulation}
                            onPause={handlePauseSimulation}
                            onWatchAgain={handleWatchAgain}
                        />
                    </div>
                </div>

                {isMapReady && (
                    <ParkingSlotList
                        variant="vertical"
                        ref={parkingSlotListRef}
                        slots={parkingSlots}
                        selectedSlotId={selectedSlotId}
                        onSelectSlot={handleSelectSlot}
                        onShowMyLocation={handleShowMyLocation}
                    />
                )}
            </section>

            {loading && <ContentLoading loading={loading} />}
        </section>
    );
}
